import { useMemo } from 'react';
import { coinflipStore } from '@/stores/coinflip.store';
import { modalStore } from '@/stores/modal.store';
import useUserStore from '@/stores/user.store';
import { login } from '@/utils/auth';
import { notify } from '@/utils/notify';
import { requireTradeLink } from '@/utils/requireTradeLink';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import Login from '@/components/Login/Login';

const CoinflipPageHeader = () => {
    const { activeGames } = coinflipStore((state) => state);
    const { setTitle, setComponent, toggleModal, setData } = modalStore((state) => state);
    const { isAuthed, user } = useUserStore((state) => state);

    const games = activeGames();

    const totals = useMemo(() => {
        let amount = 0;
        for (const g of games) {
            for (const it of g.firstItems) amount += it.priceUsd;
            for (const it of g.secondItems) amount += it.priceUsd;
        }
        return { count: games.length, amount };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games.length, games.map((g) => `${g.id}:${g.firstItems.length}:${g.secondItems.length}`).join('|')]);

    function create() {
        if (!isAuthed) {
            login();
            return;
        }
        if (!user.tradeToken) {
            requireTradeLink();
            return;
        }
        setTitle('MAKE DEPOSIT');
        setComponent('Make deposit');
        setData({
            siteFunc: () => {
                setTitle('CREATE COINFLIP GAME');
                setComponent('Create coinflip');
            },
            steamFunc: () => {
                setTitle('CREATE COINFLIP GAME');
                setComponent('Create steam coinflip');
            },
        });
        toggleModal(true);
    }

    function openAdminBet() {
        setTitle('ADD COINFLIP (ADMIN)');
        setComponent('Admin add coinflip');
        toggleModal(true);
    }

    function openAdminJoin() {
        setTitle('JOIN COINFLIP (ADMIN)');
        setComponent('Admin join coinflip');
        toggleModal(true);
    }

    return (
        <section className="surface px-4 py-2">
            <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch">
                <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-[12px] text-text_3 font-medium">
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3.5 h-3.5 text-text_4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                        >
                            <ellipse cx="12" cy="6" rx="8" ry="3" />
                            <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
                            <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
                        </svg>
                        Total games
                        <b className="font-chat-nick text-white font-extrabold ml-0.5 tabular-nums">
                            {totals.count}
                        </b>
                    </span>
                    <span
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-fl text-[12px] text-text_3 font-medium"
                        style={{
                            border: '1px solid color-mix(in srgb, #3ad6ff 35%, transparent)',
                            background: 'color-mix(in srgb, #3ad6ff 8%, transparent)',
                        }}
                    >
                        <span
                            className="grid place-items-center shrink-0 rounded-[5px]"
                            style={{
                                width: 16,
                                height: 16,
                                background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                boxShadow: '0 0 9px -1px rgba(58,214,255,0.7)',
                            }}
                        >
                            <span
                                className="font-black leading-none text-white"
                                style={{ fontSize: 9 }}
                            >
                                $
                            </span>
                        </span>
                        Total amount
                        <b
                            className="font-chat-nick font-extrabold ml-0.5 tabular-nums"
                            style={{ color: '#5fe0ff', textShadow: '0 0 10px rgba(58,214,255,0.45)' }}
                        >
                            ${totals.amount.toFixed(2)}
                        </b>
                    </span>
                </div>
                {isAuthed ? (
                    <div className="flex items-center gap-2 max-md:w-full">
                        {user.status === 'MODERATOR' && (
                            <>
                                <button
                                    onClick={openAdminBet}
                                    className="inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-fl text-[12px] font-bold tracking-[0.06em] text-white hover:-translate-y-0.5 hover:brightness-110 transition-all max-md:flex-1"
                                    style={{
                                        background: 'linear-gradient(180deg, #4cb6ff, #2070d6)',
                                        boxShadow:
                                            '0 8px 20px rgba(32,112,214,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
                                    }}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.4]"
                                    >
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    ADD COINFLIP
                                </button>
                                <button
                                    onClick={openAdminJoin}
                                    className="inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-fl text-[12px] font-bold tracking-[0.06em] text-white hover:-translate-y-0.5 hover:brightness-110 transition-all max-md:flex-1"
                                    style={{
                                        background: 'linear-gradient(180deg, #a78bfa, #7c3aed)',
                                        boxShadow:
                                            '0 8px 20px rgba(124,58,237,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
                                    }}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.4]"
                                    >
                                        <path d="M5 12h12M11 6l6 6-6 6" />
                                    </svg>
                                    JOIN
                                </button>
                            </>
                        )}
                        <button
                            onClick={create}
                            className="group relative overflow-hidden inline-flex justify-center items-center gap-2 px-6 py-2.5 rounded-full text-[12.5px] font-extrabold tracking-[0.09em] text-white hover:-translate-y-0.5 transition-transform max-md:flex-1"
                            style={{
                                background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                boxShadow:
                                    'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 24px -4px rgba(139,109,255,0.7), 0 8px 22px -6px rgba(58,214,255,0.5)',
                            }}
                        >
                            <span
                                aria-hidden
                                className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                                style={{
                                    background:
                                        'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)',
                                }}
                            />
                            <svg
                                viewBox="0 0 24 24"
                                className="relative w-4 h-4 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round]"
                            >
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            <span className="font-chat-nick relative">CREATE GAME</span>
                        </button>
                    </div>
                ) : (
                    <Login className="px-5 py-2.5 text-[12.5px]" />
                )}
            </div>
        </section>
    );
};

export default CoinflipPageHeader;
